import { useQuery } from "@tanstack/react-query";
import { useIndexedDBStore } from "../libs/use-indexeddb/";
import moment from "moment";
import { dataset as f15Data } from "../data/f15";
import { dataset as m15Data } from "../data/m15";
import { useAppStore } from "../store/appStore";

const rankChartInterval = 12;

const sortVotes = (left, right) => {
	if (left.likes > right.likes) {
		return -1;
	} else if (left.likes < right.likes) {
		return 1;
	} else {
		return left.id < right.id ? -1 : 1;
	}
};

const reductions = {
	f: [
		{ id: 2, votes: 40223 },
		{ id: 3, votes: 0 },
		{ id: 4, votes: 0 },
		{ id: 5, votes: 159694 },
		{ id: 6, votes: 118 },
		{ id: 7, votes: 0 },
		{ id: 9, votes: 0 },
		{ id: 12, votes: 50784 },
		{ id: 14, votes: 0 },
		{ id: 15, votes: 0 },
		{ id: 17, votes: 10639 },
		{ id: 20, votes: 0 },
		{ id: 22, votes: 0 },
		{ id: 25, votes: 0 },
		{ id: 26, votes: 0 },
	],
	m: [
		{ id: 27, votes: 2354 },
		{ id: 28, votes: 314 },
		{ id: 30, votes: 0 },
		{ id: 32, votes: 4532 },
		{ id: 33, votes: 3254 },
		{ id: 34, votes: 16309 },
		{ id: 36, votes: 5302 },
		{ id: 37, votes: 2827 },
		{ id: 40, votes: 0 },
		{ id: 42, votes: 0 },
		{ id: 43, votes: 0 },
		{ id: 45, votes: 1530 },
		{ id: 46, votes: 0 },
		{ id: 49, votes: 3438 },
		{ id: 51, votes: 307 },
	],
};

export const useQueryVoteResult = ({ listOption, sortEnabled }) => {
	const queryFn = async () => {
		const currentRaw = await fetch(
			"https://raw.githubusercontent.com/sonoy/tne-the-next-era-2022-stats-dataset/main/top30/1660399233.json"
		);
		const currentData = await currentRaw.json();

		const result =
			listOption === "f" ? currentData.fResult : currentData.mResult;

		const reduction = listOption === "f" ? reductions.f : reductions.m;

		result.forEach((choice, index) => {
			result[index].originalLikes = choice.likes;
			result[index].reduction = reduction.filter(
				({ id }) => id === choice.id
			)[0].votes;
			result[index].likes = choice.likes - result[index].reduction;
		});

		const sortedResult = [...result].sort(sortVotes);

		result.forEach((choice, index) => {
			result[index].rank =
				sortedResult.findIndex((item) => item.id === choice.id) + 1;
		});

		return {
			records: sortEnabled
				? [
						...result.sort((left, right) => {
							return left.rank < right.rank ? -1 : 1;
						}),
				  ]
				: result,
			lastRefreshTime: moment("2022-08-14T01:00:00"),
		};
	};

	return useQuery(["top30VoteResult", { listOption, sortEnabled }], queryFn, {
		retry: false,
	});
};

const sortVotesTrend = (left, right) => {
	return left.y < right.y ? -1 : 1;
};

export const useQueryTrend = ({ listOption }) => {
	const setQueryTop30TrendsProgress = useAppStore(
		(state) => state.setQueryTop30TrendsProgress
	);

	const { getByID, add } = useIndexedDBStore("top21-grouped");

	const queryFn = async () => {
		setQueryTop30TrendsProgress(listOption, 0);

		const dataFiles = [
			"1659931200.json",
			"1660017600.json",
			"1660104000.json",
			"1660190400.json",
			"1660276800.json",
			"1660363200.json",
		];

		// ---- rank trends
		const rankTrendsTimeGroups = [];

		const now = moment();
		const first = moment("2022-08-08T12:00:00");
		const estFinal = moment("2022-08-13T22:00:00");
		const final = now.isBefore(estFinal) ? now : estFinal;

		let rankTrendsGroupTime = moment(first);

		while (rankTrendsGroupTime.isBefore(final)) {
			rankTrendsTimeGroups.push({
				time: moment(rankTrendsGroupTime),
				unixTime: moment(rankTrendsGroupTime).unix(),
			});
			rankTrendsGroupTime = moment(rankTrendsGroupTime).add(
				rankChartInterval,
				"hour"
			);
		}
		rankTrendsTimeGroups.push({
			time: final,
			unixTime: final.unix(),
		});
		// -----

		const result = (listOption === "f" ? [...f15Data] : [...m15Data]).map(
			(item) => {
				return {
					...item,
					voteTrends: [],
					rankTrends: [
						...rankTrendsTimeGroups.map((group) => ({
							x: moment.unix(group.unixTime).format("DD MMM HH:mm"),
							unixTime: group.unixTime,
						})),
					],
				};
			}
		);

		let lastRefreshTime = null;

		const allData = {};

		for (var filename of dataFiles) {
			const cahceKey = `top30-grouped/${filename}`;

			let data = null;

			try {
				const cachedDataRaw = await getByID(cahceKey);
				const cachedData = JSON.parse(cachedDataRaw.content);
				data = cachedData;
			} catch (e) {
				console.log(`fail to get ${cahceKey} from IndexedDB`, e);
			}

			if (!data) {
				const dlRaw = await fetch(
					`https://raw.githubusercontent.com/sonoy/tne-the-next-era-2022-stats-dataset/main/top30-grouped/${filename}`
				);
				const dlData = await dlRaw.json();
				try {
					await add({ id: cahceKey, content: JSON.stringify(dlData) });
				} catch (e) {
					console.log(`fail to save ${cahceKey} into IndexedDB`, e);
				}

				data = dlData;
			}

			data.forEach((timeData) => {
				allData[timeData.unixTime] = timeData;
			});

			setQueryTop30TrendsProgress(
				listOption,
				((dataFiles.indexOf(filename) + 1) / dataFiles.length) * 50
			);
		}

		const allDataKeys = Object.keys(allData);
		let lastVoteResult = null;

		for (var fileIdx of allDataKeys) {
			let data = allData[fileIdx];

			const voteResult = listOption === "f" ? data.fResult : data.mResult;
			lastVoteResult = voteResult;

			const sortedVoteResult = [...voteResult].sort(sortVotes);

			result.forEach((choice, i) => {
				const filteredVoteResult = voteResult.filter(
					({ id }) => choice.id === id
				);
				if (filteredVoteResult.length > 0) {
					result[i].voteTrends.push({
						x: moment.unix(data.unixTime).format("YYYY-MM-DD HH:mm:00"),
						y: filteredVoteResult[0].likes,
					});

					result[i].rankTrends.forEach((trendGroup, groupIdx) => {
						if (
							moment
								.unix(data.unixTime)
								.isSameOrBefore(moment.unix(trendGroup.unixTime))
						) {
							result[i].rankTrends[groupIdx].y =
								sortedVoteResult.findIndex(({ id }) => choice.id === id) + 1;
						}
					});
				}
			});

			setQueryTop30TrendsProgress(
				listOption,
				50 + ((allDataKeys.indexOf(fileIdx) + 1) / allDataKeys.length) * 50
			);
		}

		const reduction = listOption === "f" ? reductions.f : reductions.m;
		const sortedFinalVoteResult = [
			...lastVoteResult.map(({ likes, ...choice }) => ({
				...choice,
				likes: likes - reduction.filter(({ id }) => id === choice.id)[0].votes,
			})),
		].sort(sortVotes);

		result.forEach((choice, i) => {
			const finalRank =
				sortedFinalVoteResult.findIndex(({ id }) => choice.id === id) + 1;

			result[i].rankTrends.push({
				x: "Final",
				y: finalRank,
			});
		});

		lastRefreshTime = moment("2022-08-14T01:00:00");

		return {
			records: [
				...result.map((choice) => {
					return {
						...choice,
						voteTrends: [...choice.voteTrends].sort(sortVotesTrend),
						rankTrends: [...choice.rankTrends],
					};
				}),
			],
			reduction,
			lastRefreshTime,
		};
	};

	return useQuery(["top30VoteTrends", { listOption }], queryFn, {
		retry: false,
	});
};
